// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { useEffect, useState } from "react";

import { useAuthSession } from "../../../hooks/useAuthSession";
import { useCurrentTenant } from "../../../hooks/useCurrentTenant";
import { readAccessTokenActorId } from "../../../utils/accessTokenClaims";
import { searchDelegations } from "../api/delegationsApi";

export function usePendingDelegationCount(isEnabled: boolean): number {
  const { getAccessToken } = useAuthSession();
  const { tenantId } = useCurrentTenant();
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isEnabled || !tenantId) {
      return;
    }

    let cancelled = false;

    const loadPendingCount = async (): Promise<void> => {
      try {
        const token = await getAccessToken().catch(() => undefined);

        if (token === undefined) {
          return;
        }

        const actorId = readAccessTokenActorId(token);

        if (actorId === undefined) {
          return;
        }

        const result = await searchDelegations(
          { token, tenantId },
          {
            status: "offered",
            delegateId: actorId,
            pageNumber: 1,
            pageSize: 1,
          },
        );

        if (!cancelled) {
          setCount(result.totalCount);
        }
      } catch {
      }
    };

    void loadPendingCount();

    return () => {
      cancelled = true;
    };
  }, [isEnabled, tenantId, getAccessToken]);

  return count;
}
